import React from 'react';
import { Select } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import SubtitleWithLines from 'components/common/SubtitleWithLines';
import { toJS } from 'mobx';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { Agency, SubAgency } from 'types/aaaService';
import { createStyles } from './styles';
import AgencySearch from './AgencySearch';

const ChangeAgencyForm = observer((): JSX.Element => {
  const classes = createStyles();

  // Mobx store
  const {
    roleTypeAndAgencyStore: {
      sponsors,
      availableAgencies,
      availableSubAgencies,
      selectedSponsor,
      selectedAgency,
      selectedSubAgency,
      isSingleAgencyWithSubagencies,
      setSelectedSponsor,
      setSelectedAgency,
      setSelectedSubAgency,
    },
  } = useRootStore();

  // Converted values from store
  const convertedSponsors = sponsors.map((sponsor) => toJS(sponsor)).slice();
  const convertedAgencies = availableAgencies.map((agency) => toJS(agency)).slice();
  const convertedSubAgencies = availableSubAgencies.map((subAgency) => toJS(subAgency)).slice();

  const convertedSelectedSponsor = selectedSponsor || toJS(selectedSponsor);
  const convertedSelectedAgency = selectedAgency || toJS(selectedAgency);
  const convertedSelectedSubAgency = selectedSubAgency || toJS(selectedSubAgency);

  // Form select options
  const mapAgencyToOption = (agencies: Agency[] | SubAgency[]): OptionType[] =>
    agencies.map((agency) => ({ label: agency.name, value: agency.id }));

  const sponsorOptions = convertedSponsors.map((sponsor) => ({ label: sponsor.name, value: sponsor.id }));
  const agencyOptions = mapAgencyToOption(availableAgencies);
  const subAgencyOptions = mapAgencyToOption(convertedSubAgencies);

  // Form components update
  const onSponsorChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const currentSponsor = convertedSponsors.find(({ id }) => id === e.target.value) || null;
    setSelectedSponsor(currentSponsor);
  };

  const onAgencyChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const currentAgency = convertedAgencies.find(({ id }) => id === e.target.value) || null;
    setSelectedAgency(currentAgency);
  };

  const onSubAgencyChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const currentSubAgency = convertedSubAgencies.find(({ id }) => id === e.target.value) || null;
    setSelectedSubAgency(currentSubAgency);
  };

  return (
    <form id="change-agency-form" className={classes.form}>
      <SubtitleWithLines text="Select sponsor and agency" className={classes.subtitle} />
      <div className={classes.formElements}>
        {convertedSponsors.length > 1 && (
          <Select
            name="sponsor"
            label="Sponsor"
            placeholder="..."
            options={sponsorOptions}
            value={convertedSelectedSponsor?.id || ''}
            onChange={onSponsorChange}
            className={classes.select}
            validationPassed={!!convertedSelectedSponsor?.id}
          />
        )}
        {/* 
          Hide the agency selection if there is only one main agency but multiple subagencies. 
          This prevents users from seeing a single option in the agency dropdown, 
          directing them to choose from the subagency dropdown labeled as "Agency." 
          This design minimizes confusion, as users won't realize they are selecting subagencies directly.
        */}
        {convertedAgencies.length > 0 && !isSingleAgencyWithSubagencies && (
          <>
            <Select
              name="agency"
              label="Agency"
              placeholder="..."
              options={agencyOptions}
              value={convertedSelectedAgency?.id || ''}
              onChange={onAgencyChange}
              className={classes.select}
              validationPassed={!!convertedSelectedAgency?.id}
            />
            {convertedAgencies.length >= 10 && <AgencySearch />}
          </>
        )}
        {convertedSubAgencies.length > 0 && (
          <Select
            name="subAgency"
            // The label is changed for better UX, but the functionality remains as a subAgency select.
            label={isSingleAgencyWithSubagencies ? 'Agency' : 'Sub-Agency'}
            placeholder="..."
            options={subAgencyOptions}
            value={convertedSelectedSubAgency?.id || ''}
            onChange={onSubAgencyChange}
            className={classes.select}
          />
        )}
      </div>
    </form>
  );
});

export default ChangeAgencyForm;
